import {
  TRACKING_MAX_CARRIERS,
  TRACKING_SAMPLES_PER_CARRIER,
} from "@redotech/admin-sdk/marketing-rpc/schema/shipment-tracking/test-tracking-samples";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoadWithInput } from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { YyyyMmDd } from "@redotech/zod-util/db-coercion";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoAdminMarketingRpcClientContext } from "../../../app/redo-admin-marketing-rpc-client-provider";

interface ShipmentTrackingModalProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
}

const DATE_RANGE_OPTIONS: RedoListItem<number>[] = [
  { id: "7", type: "text", text: "Last 7 days", value: 7 },
  { id: "30", type: "text", text: "Last 30 days", value: 30 },
  { id: "90", type: "text", text: "Last 90 days", value: 90 },
];

export const ShipmentTrackingModal = memo(function ShipmentTrackingModal({
  open,
  onClose,
  teamId,
}: ShipmentTrackingModalProps) {
  const marketingRpcClient = useRequiredContext(
    RedoAdminMarketingRpcClientContext,
  );

  const [dateRange, setDateRange] = useState(30);

  const selectedDateRangeItem = useMemo(
    () => DATE_RANGE_OPTIONS.find((o) => o.value === dateRange),
    [dateRange],
  );

  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - dateRange);
    return {
      startDate: start.toISOString().split("T")[0] as YyyyMmDd,
      endDate: end.toISOString().split("T")[0] as YyyyMmDd,
    };
  }, [dateRange]);

  const distributionLoad = useLoad(
    async (signal) => {
      if (!open) {
        return null;
      }
      const result = await marketingRpcClient.getTeamCarrierDistribution(
        { teamId, startDate, endDate },
        { signal },
      );
      return result;
    },
    [open, teamId, startDate, endDate],
  );

  const carriers = useMemo(
    () => distributionLoad.value?.carriers ?? [],
    [distributionLoad.value?.carriers],
  );
  const totalShipments = useMemo(
    () => carriers.reduce((sum, c) => sum + c.count, 0),
    [carriers],
  );

  const [testSamplesLoad, doTestSamples] = useTriggerLoadWithInput(
    async (carrierNames: string[]) => {
      const result = await marketingRpcClient.testTrackingSamples({
        teamId,
        carriers: carrierNames,
        samplesPerCarrier: TRACKING_SAMPLES_PER_CARRIER,
      });
      return result.results;
    },
  );

  const handleTestSamples = useCallback(() => {
    if (carriers.length === 0) {
      return;
    }
    const carrierNames = carriers
      .slice(0, TRACKING_MAX_CARRIERS)
      .map((c) => c.rawCarrier);
    doTestSamples(carrierNames);
  }, [carriers, doTestSamples]);

  if (!open) {
    return null;
  }

  return (
    <RedoModal
      modalSize="lg"
      onModalCloseRequested={onClose}
      title="Shipment tracking analysis"
    >
      <Flex dir="column" gap="xl" p="lg" style={{ minHeight: "400px" }}>
        <Flex align="center" gap="lg">
          <Text fontSize="sm">Date range:</Text>
          <RedoSingleSelectDropdownInput
            options={DATE_RANGE_OPTIONS}
            optionSelected={({ item }) => setDateRange(item.value)}
            selectedItem={selectedDateRangeItem}
          />
        </Flex>

        {distributionLoad.pending ? (
          <Flex align="center" justify="center" style={{ flex: 1 }}>
            <RedoSpinner />
          </Flex>
        ) : distributionLoad.error ? (
          <Text textColor="error">
            Error loading data: {distributionLoad.error.message}
          </Text>
        ) : (
          <>
            <Flex dir="column" gap="md">
              <Text fontSize="md">Carrier distribution</Text>
              <Text fontSize="sm" textColor="secondary">
                Total shipments: {totalShipments.toLocaleString()}
              </Text>

              {carriers.length === 0 ? (
                <Text textColor="secondary">
                  No shipment data found for this period.
                </Text>
              ) : (
                <div
                  style={{
                    maxHeight: "250px",
                    overflowY: "auto",
                    border:
                      "1px solid var(--redo-colors-border-border-primary)",
                    borderRadius: "var(--border-radius-lg)",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr
                        style={{
                          borderBottom:
                            "1px solid var(--redo-colors-border-border-primary)",
                          position: "sticky",
                          top: 0,
                          background:
                            "var(--redo-colors-background-bg-primary)",
                        }}
                      >
                        <th
                          style={{
                            padding:
                              "var(--redo-spacing-md) var(--redo-spacing-lg)",
                            textAlign: "left",
                            fontWeight: 500,
                          }}
                        >
                          Raw Carrier
                        </th>
                        <th
                          style={{
                            padding:
                              "var(--redo-spacing-md) var(--redo-spacing-lg)",
                            textAlign: "left",
                            fontWeight: 500,
                          }}
                        >
                          Mapped carrier
                        </th>
                        <th
                          style={{
                            padding:
                              "var(--redo-spacing-md) var(--redo-spacing-lg)",
                            textAlign: "right",
                            fontWeight: 500,
                          }}
                        >
                          Count
                        </th>
                        <th
                          style={{
                            padding:
                              "var(--redo-spacing-md) var(--redo-spacing-lg)",
                            textAlign: "right",
                            fontWeight: 500,
                          }}
                        >
                          %
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {carriers.map((carrier, index) => (
                        <tr
                          key={carrier.rawCarrier}
                          style={{
                            borderBottom:
                              index < carriers.length - 1
                                ? "1px solid var(--redo-colors-border-border-secondary)"
                                : "none",
                          }}
                        >
                          <td
                            style={{
                              padding:
                                "var(--redo-spacing-md) var(--redo-spacing-lg)",
                            }}
                          >
                            {carrier.rawCarrier}
                          </td>
                          <td
                            style={{
                              padding:
                                "var(--redo-spacing-md) var(--redo-spacing-lg)",
                            }}
                          >
                            {carrier.resolvedCarrier ?? (
                              <Text as="span" textColor="warning">
                                unknown
                              </Text>
                            )}
                          </td>
                          <td
                            style={{
                              padding:
                                "var(--redo-spacing-md) var(--redo-spacing-lg)",
                              textAlign: "right",
                            }}
                          >
                            {carrier.count.toLocaleString()}
                          </td>
                          <td
                            style={{
                              padding:
                                "var(--redo-spacing-md) var(--redo-spacing-lg)",
                              textAlign: "right",
                            }}
                          >
                            {totalShipments > 0
                              ? (
                                  (carrier.count / totalShipments) *
                                  100
                                ).toFixed(1)
                              : 0}
                            %
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Flex>

            {carriers.length > 0 && (
              <Flex dir="column" gap="md">
                <Flex align="center" gap="md">
                  <RedoButton
                    disabled={testSamplesLoad.pending}
                    hierarchy="secondary"
                    onClick={handleTestSamples}
                    pending={testSamplesLoad.pending}
                    size="sm"
                    text={
                      testSamplesLoad.pending ? "Testing..." : "Test tracking"
                    }
                  />
                  <Text fontSize="xs" textColor="secondary">
                    Tests up to {TRACKING_SAMPLES_PER_CARRIER} samples per
                    carrier (max {TRACKING_MAX_CARRIERS} carriers)
                  </Text>
                </Flex>

                {testSamplesLoad.error && (
                  <Text textColor="error">
                    Error testing samples: {testSamplesLoad.error.message}
                  </Text>
                )}

                {testSamplesLoad.value && (
                  <div
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      border:
                        "1px solid var(--redo-colors-border-border-primary)",
                      borderRadius: "var(--border-radius-lg)",
                      padding: "var(--redo-spacing-lg)",
                    }}
                  >
                    {testSamplesLoad.value.map((carrierResult) => (
                      <div
                        key={carrierResult.carrier}
                        style={{ marginBottom: "var(--redo-spacing-xl)" }}
                      >
                        <Text fontSize="sm">{carrierResult.carrier}</Text>
                        {carrierResult.samples.length === 0 ? (
                          <Text fontSize="xs" textColor="secondary">
                            No samples found
                          </Text>
                        ) : (
                          <div
                            style={{
                              marginTop: "var(--redo-spacing-xs)",
                              display: "grid",
                              gridTemplateColumns: "16px auto 1fr",
                              gap: "var(--redo-spacing-xs) var(--redo-spacing-md)",
                              alignItems: "center",
                            }}
                          >
                            {carrierResult.samples.map((sample, idx) => {
                              const hasWarning =
                                sample.usedFallback ||
                                sample.status === "unknown";
                              return (
                                <>
                                  <Text
                                    fontSize="xs"
                                    fontWeight="medium"
                                    key={`${idx}-icon`}
                                    style={{ textAlign: "center" }}
                                    textColor={
                                      sample.success
                                        ? hasWarning
                                          ? "warning"
                                          : "success"
                                        : "error"
                                    }
                                  >
                                    {sample.success
                                      ? hasWarning
                                        ? "!"
                                        : "✓"
                                      : "✗"}
                                  </Text>
                                  <Text
                                    as="code"
                                    fontFamily="monospace"
                                    fontSize="xs"
                                    key={`${idx}-code`}
                                    style={{
                                      background:
                                        "var(--redo-colors-background-bg-secondary)",
                                      padding:
                                        "var(--redo-spacing-xxs) var(--redo-spacing-sm)",
                                      borderRadius: "var(--border-radius-sm)",
                                      width: "fit-content",
                                    }}
                                  >
                                    {sample.trackingCode}
                                  </Text>
                                  <Flex gap="sm" key={`${idx}-msg`}>
                                    {sample.success ? (
                                      <>
                                        <Text
                                          fontSize="xs"
                                          textColor={
                                            sample.status === "unknown"
                                              ? "warning"
                                              : "secondary"
                                          }
                                        >
                                          {sample.status} via {sample.provider}
                                        </Text>
                                        {sample.usedFallback && (
                                          <Text
                                            fontSize="xs"
                                            textColor="warning"
                                          >
                                            Primary failed:{" "}
                                            {sample.primaryError}
                                          </Text>
                                        )}
                                      </>
                                    ) : (
                                      <Text fontSize="xs" textColor="error">
                                        {sample.error}
                                      </Text>
                                    )}
                                  </Flex>
                                </>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Flex>
            )}
          </>
        )}
      </Flex>
    </RedoModal>
  );
});
